/* eslint-disable react-hooks/exhaustive-deps */

import { Alert, Backdrop, Button, CircularProgress, Snackbar, useMediaQuery } from '@mui/material';
import { Buffer } from 'buffer';
import { debounce } from 'lodash';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Iframe from 'react-iframe';
import { useNavigate } from 'react-router-dom';
import { builderURL, copyFailure, copySuccess, waymoreFormsURL } from "../../Utils/common";
import { formContent } from './interface';

import moment from "moment/moment.js";
import appContext from '../../appContext';
import ListEmpty from '../../components/Custom/EmptyStates/ListEmpty';
import NoResultFound from '../../components/Custom/EmptyStates/NoResultFound';
import TrashEmpty from '../../components/Custom/EmptyStates/TrashEmpty';
import DeleteConfirmationModal from '../../components/Custom/Modals/DeleteConfirmationModal';
import ActionsPopover from './ActionsPopover';
import CardContainer from './CardContainer';
import GlobalFooter from './GlobalFooter';
import GlobalHeader from './GlobalHeader';
import GlobalSidebar from './GlobalSidebar';
import SkeletonCard from './SkeletonCard';
import WorkspaceHeader from './WorkspaceHeader';

import {
  createAccessLink,
  duplicateFormByUUID,
  getForms,
  getPreviewLink,
  getUserAccess,
  multiDeleteForms,
  publishForms,
  restoreForm,
} from "../../Services/API";

import {
  BodyContainer,
  GlobalBodyContainer,
  GlobalContentContainer,
  GlobalHeaderContainer,
  GlobalRootContainer,
  GlobalSidebarContainer,
  RootContainer,
} from "./styled";

import {
  getCardLastEditedOn,
  getOrganizedDataProvider,
  sidebarValueConstants,
} from "./sidebarutils";

import CreateNewFormPopup from '../../components/Popup/createNewFormPopup';

import "./style.scss";

function Workspace() {
  const navigate = useNavigate()
  const footerRef = useRef<HTMLDivElement>(null);
  const isLessThan1280 = useMediaQuery("(max-width:1279px)");

  const {
    selectedMode,
    setSelectedMode,
    isListEmpty,
    setIsListEmpty,
    isListSearchActive,
    setIsListSearchActive,
  } = useContext(appContext);

  const [dataProvider, setDataProvider] = useState<any>({
    deleted: [],
    drafted: [],
    published: [],
  });

  const [elementIndex, setElementIndex] = useState<number>();
  const [sortOption, setSortOption] = useState<string>('newest');
  const [sortByDateOption, setSortByDateOption] = useState<string>('created');
  const [filterOption, setFilterOption] = useState<string>('formName');
  const [searchValue, setSearchValue] = useState<string>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [multiDelete, setMultiDelete] = useState<boolean>(false);
  const [multiDeleteArr, setMultiDeleteArr] = useState<string[]>([]);
  const [tosterOpen, setTosterOpen] = useState<any>({ open: false, message: "", severity: "", undoAction: null })

  // Ref to hold the pending hard-delete timeout so UNDO can cancel it
  const pendingHardDeleteRef = useRef<any>(null);
  // Sidebar collapse state — open by default on wide screens, collapsed on narrow
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const [createNewFormPopup, setCreateNewFormPopup] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<any>(null);

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [forms, setForms] = useState<any[]>([]);
  const [loadSkeleton, setLoadSkeleton] = useState<boolean>(true);

  // Backend counts for sidebar (real counts, not from loaded forms)
  const [formCounts, setFormCounts] = useState<{ published: number; drafted: number; deleted: number }>({
    published: 0,
    drafted: 0,
    deleted: 0,
  });

  // Dynamic grid columns based on screen size
  const [gridColumns, setGridColumns] = useState<number>(4);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate columns based on container width (grid uses minmax(270px, 1fr))
  useEffect(() => {
    const calculateColumns = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const minCardWidth = 270;
        const gap = 30; // gap from CSS
        // Calculate how many cards fit: (width + gap) / (minWidth + gap)
        const cols = Math.floor((containerWidth + gap) / (minCardWidth + gap));
        setGridColumns(Math.max(1, cols));
      }
    };

    calculateColumns();

    // Use ResizeObserver to track container size changes
    const resizeObserver = new ResizeObserver(calculateColumns);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', calculateColumns);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateColumns);
    };
  }, []);

  // Calculate skeleton count based on columns (fill 2 rows), PAGE_SIZE is fixed
  const skeletonCount = useMemo(() => gridColumns * 2, [gridColumns]);
  const PAGE_SIZE = 20; // Fixed page size to prevent re-fetching on resize

  // Intersection Observer ref
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Flag to prevent duplicate initial fetches
  const hasFetchedRef = useRef<boolean>(false);

  // Ref to track if we're currently fetching (avoids stale closure issues)
  const isFetchingRef = useRef<boolean>(false);

  // Flag to track if component is still initializing (prevents multiple calls during mount)
  const isInitializingRef = useRef<boolean>(true);

  useEffect(() => {
    setIsListSearchActive(Boolean(searchValue));
  }, [searchValue]);

  useEffect(() => {
    if (selectedMode === sidebarValueConstants.deleted) {
      setIsListEmpty(!Boolean(dataProvider.deleted.length));
    } else if (selectedMode === sidebarValueConstants.drafted) {
      setIsListEmpty(!Boolean(dataProvider.drafted.length));
    } else if (selectedMode === sidebarValueConstants.published) {
      setIsListEmpty(!Boolean(dataProvider.published.length));
    } else {
      setIsListEmpty(false);
    }
  }, [dataProvider, selectedMode]);

  const handleClick = async (event: any, formUUID: string, formData: any) => {
    try {
      if (event === "Edit") {
        navigate('/createform', { state: { formUUID, dataFromModal: formData } })
      } else if (event === 'Preview') {
        setOpen(false);
        setIsLoading(true);

        const formDetails = await getPreviewLink(formUUID)

        if (formDetails?.formLink) {
          window.open(formDetails?.formLink, '_blank');
        } else {
          setTosterOpen({ open: true, message: "Something went wrong", severity: "error" })
        }

        setIsLoading(false);
      } else if (event === 'Delete') {
        setMultiDeleteArr([formUUID])
        setMultiDelete(true)
        setOpen(false);
        setOpenDeletePopup(true)
      } else if (event === 'Copy Edit link') {
        setOpen(false);
        setIsLoading(true);

        const payload: any = {
          userId: sessionStorage?.principalId,
          iframeId: 1,
          configuration: {
            formUUID,
            userId: sessionStorage?.principalId,
            formData: Buffer.from(JSON.stringify(formData || {})).toString('base64')

          },
          expires: moment(new Date()).add(1, "day").format('YYYY-MM-DD hh:mm:ss')
        }

        const formDetails = await getUserAccess(payload);

        setIsLoading(false);

        if (formDetails?.formUUID) {
          let copyText = `${builderURL}/?id=${formDetails.formUUID}`
          let toastState: any = {}
          if (navigator.clipboard) {
            try {
              let toastState: any = {}
              if (navigator.clipboard) {
                try {
                  navigator.clipboard.writeText(copyText)
                  toastState = copySuccess
                } catch (e) {
                  console.log("error", e)
                  toastState = copyFailure
                }
              } else {
                const textArea = document.createElement('textarea');
                textArea.value = copyText;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                  document.execCommand('copy');
                  toastState = copySuccess

                } catch (err) {
                  toastState = copyFailure
                  console.error('Failed to copy: ', err);
                }
                document.body.removeChild(textArea);
              }
              setTosterOpen(toastState)
            } catch (e) {
              console.log("error", e)
              toastState = copyFailure
            }
          } else {
            const textArea = document.createElement('textarea');
            textArea.value = copyText;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              document.execCommand('copy');
              toastState = copySuccess

            } catch (err) {
              toastState = copyFailure
              console.error('Failed to copy: ', err);
            }
            document.body.removeChild(textArea);
          }
          setTosterOpen(toastState)
        } else {
          setTosterOpen(copyFailure)
        }

      } else if (event === 'Duplicate') {
        setOpen(false);
        setIsLoading(true);

        duplicateFormByUUID(formUUID)
          .then(() => {
            setTosterOpen({
              open: true,
              severity: "success",
              message: "Duplicated successfully",
            });
            getFormDatas(1, true);
          })
          .catch(() => {
            setIsLoading(false);
            setTosterOpen({
              open: true,
              severity: "error",
              message: "Error duplicating",
            });
          });
      } else if (event === 'Select') {
        setMultiDeleteArr([formUUID])
        setMultiDelete(true)
      } else if (event === 'Move to draft') {
        handleMoveToDraft([formUUID])
      } else if (event === 'Publish') {
        publishForm([formUUID])
      } else if (event === "Delete forever") {
        setMultiDeleteArr([formUUID])
        setOpenDeletePopup(true)
      } else if (event === 'Copy link') {
        setOpen(false);
        if (formData?.formLink) {
          let toastState: any = {}
          if (navigator.clipboard) {
            try {
              navigator.clipboard.writeText(formData.formLink)
              toastState = copySuccess
            } catch (e) {
              console.log("error", e)
              toastState = copyFailure
            }
          } else {
            const textArea = document.createElement('textarea');
            textArea.value = formData.formLink;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              document.execCommand('copy');
              toastState = copySuccess

            } catch (err) {
              toastState = copyFailure
              console.error('Failed to copy: ', err);
            }
            document.body.removeChild(textArea);
          }
          setTosterOpen(toastState)
        } else {
          setTosterOpen(copyFailure)
        }
      } else {
        const formDetails = await getPreviewLink(formUUID)
        if (formDetails.formLink) {
          navigator.clipboard.writeText(formDetails.formLink)
          let toastState: any = {}
          if (navigator.clipboard) {
            try {
              navigator.clipboard.writeText(formData.formLink)
              toastState = copySuccess
            } catch (e) {
              console.log("error", e)
              toastState = copyFailure
            }
          } else {
            const textArea = document.createElement('textarea');
            textArea.value = formData.formLink;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              document.execCommand('copy');
              toastState = copySuccess

            } catch (err) {
              toastState = copyFailure
              console.error('Failed to copy: ', err);
            }
            document.body.removeChild(textArea);
          }
          setTosterOpen(toastState)
        } else {
          setTosterOpen(copyFailure)
        }
      }
      handleClose();
    } catch (e) {
      console.log("err", e)
      handleClose();
    }
  }

  const publishForm = async (formUUIDs: any) => {
    await publishForms(formUUIDs)
    setTosterOpen({
      open: true,
      severity: "success",
      message: "Published successfully",
    });
    getFormDatas(1, true);
    setMultiDelete(false)
  }

  const handleMoveToDraft = async (formUUIDs: any) => {
    await restoreForm(formUUIDs)
    setTosterOpen({
      open: true,
      severity: "success",
      message: "Moved to draft successfully",
    });
    getFormDatas(1, true);
    setMultiDelete(false)
  }

  const handleClose = () => {
    setOpen(false);
  };

  // Memoized callback for sidebar to prevent re-renders
  const handleSidebarChange = useCallback((value: string) => {
    setSelectedMode(value);
    setMultiDeleteArr([]);
  }, [setSelectedMode]);

  const getFormDatas = async (pageNumber: number = 1, isReset: boolean = false) => {
    // Prevent duplicate calls using ref (avoids stale closure issues)
    if (isFetchingRef.current && !isReset) return;

    isFetchingRef.current = true;
    setIsLoading(true);
    if (isReset) {
      setIsInitialLoad(true);
      setForms([]);
    }

    try {
      const response = await getForms(
        searchValue,
        sortOption,
        sortByDateOption,
        filterOption,
        filterValue,
        sessionStorage?.principalId,
        pageNumber,
        PAGE_SIZE
      );

      const formData = response?.data || [];
      const pagination = response?.pagination;
      const statusCounts = response?.statusCounts;

      // Update counts from backend only on initial load or reset (not on pagination)
      if (statusCounts && (isReset || pageNumber === 1)) {
        setFormCounts({
          published: statusCounts.published ?? 0,
          drafted: statusCounts.draft ?? 0,
          deleted: statusCounts.deleted ?? 0,
        });
      }

      if (isReset || pageNumber === 1) {
        const uniqueFormData = formData.filter((value: any, index: any, self: any) => {
          return index === self.findIndex((t: any) => t._id === value._id);
        });
        setForms(uniqueFormData);
        setDataProvider(getOrganizedDataProvider(uniqueFormData));
      } else {
        setForms((prev: any[]) => {
          const combined = [...prev, ...formData];
          const uniqueCombined = combined.filter((value: any, index: any, self: any) => {
            return index === self.findIndex((t: any) => t._id === value._id);
          });
          setDataProvider(getOrganizedDataProvider(uniqueCombined));
          return uniqueCombined;
        });
      }

      setHasMore(pagination?.hasMore ?? formData.length === PAGE_SIZE);
      setPage(pageNumber);
      setIsInitialLoad(false);
      // Mark initialization as complete after first successful fetch
      isInitializingRef.current = false;
    } catch (error) {
      console.error('Error fetching forms:', error);
      setTosterOpen({ open: true, message: "Error loading forms", severity: "error" });
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const handleSelectAll = (formUUID: string) => {
    let multiDeleteArrClone = [...multiDeleteArr]
    if (multiDeleteArrClone.includes(formUUID)) {
      multiDeleteArrClone = multiDeleteArrClone.filter((selectUUID) => selectUUID !== formUUID)
    } else {
      multiDeleteArrClone.push(formUUID)
    }
    setMultiDeleteArr(multiDeleteArrClone)
  }

  const handleMultiDelete = async (type: string | undefined) => {
    if (multiDeleteArr?.length) {
      const deletedIds = [...multiDeleteArr];
      const isHardDelete = selectedMode === sidebarValueConstants.deleted;

      // Close popover and clear anchorEl BEFORE removing DOM elements
      // to prevent MUI Menu from accessing a stale DOM reference
      setOpen(false);
      setAnchorEl(null);

      if (isHardDelete) {
        // --- HARD DELETE: deferred pattern ---
        // 1. Remove from UI immediately (forms still exist on server)
        setForms(prev => prev.filter(form => !deletedIds.includes(form.formUUID)));
        setDataProvider((prev: any) => ({
          ...prev,
          deleted: prev.deleted.filter((form: any) => !deletedIds.includes(form.formUUID))
        }));
        setMultiDelete(false);
        setMultiDeleteArr([]);
        setOpenDeletePopup(false);

        // 2. Schedule the real hard-delete API call
        if (pendingHardDeleteRef.current) {
          clearTimeout(pendingHardDeleteRef.current.timerId);
        }
        const timerId = setTimeout(async () => {
          try {
            await multiDeleteForms(deletedIds, 'hard');
          } catch (e) {
            console.error('Hard delete failed:', e);
          } finally {
            pendingHardDeleteRef.current = null;
          }
        }, 6500); // slightly longer than snackbar so it fires after close

        pendingHardDeleteRef.current = { timerId, ids: deletedIds };

        // 3. Show toast with UNDO
        setTosterOpen({
          open: true,
          severity: 'success',
          message: 'Deleted successfully',
          undoAction: () => {
            // Cancel the pending hard-delete
            if (pendingHardDeleteRef.current) {
              clearTimeout(pendingHardDeleteRef.current.timerId);
              pendingHardDeleteRef.current = null;
            }
            setTosterOpen({ open: false, message: "", severity: "", undoAction: null });
            // Refresh from server (forms are still there)
            getFormDatas(1, true);
            setTimeout(() => {
              setTosterOpen({ open: true, message: "Restored successfully", severity: "success", undoAction: null });
            }, 300);
          }
        });

      } else {
        // --- SOFT DELETE: call API immediately ---
        const fromMode = selectedMode; // capture current mode
        multiDeleteForms(multiDeleteArr, 'soft').then((res) => {
          if (res.statusCode === 200) {
            setMultiDelete(false);
            setMultiDeleteArr([]);
            setOpenDeletePopup(false);

            const undoAction = async () => {
              try {
                // restoreForm moves from deleted → draft
                await restoreForm(deletedIds);
                // If originally published, re-publish after restoring
                if (fromMode === sidebarValueConstants.published) {
                  await publishForms(deletedIds);
                }
                setTosterOpen({ open: false, message: "", severity: "", undoAction: null });
                getFormDatas(1, true);
                setTimeout(() => {
                  setTosterOpen({ open: true, message: "Restored successfully", severity: "success", undoAction: null });
                }, 300);
              } catch (e) {
                console.error('Undo failed:', e);
                setTosterOpen({ open: true, severity: 'error', message: 'Undo failed', undoAction: null });
              }
            };

            setTosterOpen({
              open: true,
              severity: 'success',
              message: 'Deleted successfully',
              undoAction
            });
            getFormDatas(1, true);
          } else {
            throw res;
          }
        }).catch((res) => {
          setTosterOpen({ open: true, severity: 'error', message: res?.err?.response?.data?.message || 'Something went wrong', undoAction: null });
          setMultiDelete(false);
          setMultiDeleteArr([]);
          setOpenDeletePopup(false);
        });
      }
    }
  }

  const handleCancel = () => {
    setMultiDeleteArr([])
    setMultiDelete(false)
    setOpenDeletePopup(false)
  }

  useEffect(() => {
    // Skip filter-triggered fetches during initialization
    // The initial fetch will be triggered by the mount effect
    if (isInitializingRef.current) {
      return;
    }

    // Filter changed after initial load - reset and refetch
    const debouncedGetFormDatas = debounce(() => {
      setPage(1);
      setHasMore(true);
      getFormDatas(1, true);
    }, 300);
    debouncedGetFormDatas();
    return () => debouncedGetFormDatas.cancel();
  }, [searchValue, sortOption, sortByDateOption, filterValue])

  // Initial mount effect - only runs once
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setPage(1);
    setHasMore(true);
    getFormDatas(1, true);
  }, []);

  useEffect(() => {
    // Skip during initial mount to avoid triggering extra fetches
    if (isInitializingRef.current) return;

    if (filterOption === "Department") {
      setFilterValue("hr")
    }
    else if (filterOption === "Application") {
      setFilterValue("feedback")
    }
    else {
      setFilterValue("")
    }
    setSearchValue("");
  }, [filterOption])

  useEffect(() => {
    if (sessionStorage.getItem('noWorkspace')) {
      navigate('/editform')
    }
    const payload: any = {
      userId: sessionStorage?.principalId,
      iframeId: 1,
      configuration: {
        userId: sessionStorage?.principalId
      },
      expires: moment(new Date()).add(1, "day").format('YYYY-MM-DD hh:mm:ss')
    }
    createAccessLink(payload).then((response) => { sessionStorage.setItem('submissionId', response?.formUUID ?? "") })
  }, [])

  const emptyStates = useMemo(() => {
    return {
      showListEmptyPage: !isListSearchActive && isListEmpty,
      showNoResultsFoundPage: isListSearchActive && isListEmpty,
      showTrashEmptyPage:
        selectedMode === sidebarValueConstants.deleted && isListEmpty,
    };
  }, [isListEmpty, isListSearchActive, selectedMode]);

  const resetSearch = () => {
    setIsLoading(true);
    setSearchValue("");
  }

  // Intersection Observer for infinite scroll
  const lastCardRef = useCallback((node: HTMLDivElement | null) => {
    // Don't observe during loading or initial load
    if (isLoading || isInitialLoad) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      // Only trigger if:
      // 1. Element is intersecting
      // 2. We have more data to load
      // 3. Not currently fetching (using ref to avoid stale closure)
      if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) {
        setPage(prev => prev + 1);
      }
    }, {
      threshold: 0.1,
      rootMargin: '100px' // Trigger a bit before reaching the end
    });

    // Delay observation to avoid immediate trigger after initial render
    if (node) {
      setTimeout(() => {
        if (observerRef.current && node && !isFetchingRef.current) {
          observerRef.current.observe(node);
        }
      }, 300);
    }
  }, [isLoading, hasMore, isInitialLoad]);

  // Fetch next page when page changes
  useEffect(() => {
    if (page > 1 && !isInitialLoad) {
      getFormDatas(page, false);
    }
  }, [page]);

  // Render skeleton cards
  const renderSkeletons = (count: number) => {
    if (loadSkeleton) {
      setLoadSkeleton(false)
    }
    debounce(() => {
      setLoadSkeleton(true)
    }, 500)
    if (isLoading) {
      if (!isInitialLoad || selectedMode === sidebarValueConstants.drafted || selectedMode === sidebarValueConstants.deleted) {
        count = 1

      }
      return Array.from({ length: count }, (_, index) => (
        <SkeletonCard key={`skeleton-${index}`} indexKey={index} />

      ));
    };
  };

  const renderCards = () => {
    // Show initial skeletons while loading first page
    if (isInitialLoad && isLoading) {
      return (
        <div className="forms-container" ref={containerRef}>
          {renderSkeletons(skeletonCount)}
        </div>
      );
    }

    if (emptyStates.showNoResultsFoundPage)
      return <NoResultFound resetSearch={resetSearch} />;

    if (emptyStates.showTrashEmptyPage) return <TrashEmpty />;
    if (emptyStates.showListEmptyPage) return <ListEmpty />;

    const currentForms = dataProvider[selectedMode] || [];

    return (
      <div className="forms-container" ref={containerRef}>
        {currentForms.map((form: formContent, index: number) => {
          const isLastCard = index === currentForms.length - 1;
          return (
            <Fragment key={form?.formUUID || index}>
              <div ref={isLastCard ? lastCardRef : null}>
                <CardContainer
                  indexKey={index}
                  cardLastEditedOn={getCardLastEditedOn(form?.updatedAt)}
                  cardImage={form?.style?.background}
                  cardColor={form?.style?.backgroundColor}
                  cardTitle={form?.formName}
                  multiDelete={multiDelete}
                  isSelected={multiDeleteArr.includes(form?.formUUID)}
                  setOpen={setOpen}
                  setAnchorEl={setAnchorEl}
                  setElementIndex={setElementIndex}
                  doubleClickHandler={() => {
                    handleClick("Edit", form?.formUUID, form);
                  }}
                  selectHandler={() => {
                    handleSelectAll(form?.formUUID);
                  }}
                />
              </div>
              <ActionsPopover
                anchorEl={anchorEl}
                onClose={handleClose}
                selectedMode={selectedMode}
                open={open && elementIndex === index && !multiDelete}
                clickHandler={(option: string) => {
                  handleClick(option, form?.formUUID, form);
                }}
              />
            </Fragment>
          );
        })}
        {/* Show skeleton cards at bottom while loading more (one row) */}
        {isLoading && !isInitialLoad && loadSkeleton && renderSkeletons(gridColumns)}
      </div>
    );
  };

  return (
    <Fragment>
      <GlobalRootContainer ref={footerRef}>
        <GlobalHeaderContainer>
          <GlobalHeader />
        </GlobalHeaderContainer>
        <GlobalBodyContainer container>
          <GlobalSidebarContainer item isCollapsed={!isSidebarOpen}>
            <GlobalSidebar
              loading={isInitialLoad}
              deletedLength={formCounts.deleted}
              draftedLength={formCounts.drafted}
              publishedLength={formCounts.published}
              selectedValue={selectedMode}
              setSelectedValue={handleSidebarChange}
              hasMore={hasMore}
              setDataProvider={setDataProvider}
              setForms={setForms}
              setIsLoading={setIsLoading}
              isCollapsed={!isSidebarOpen}
              onToggle={() => setIsSidebarOpen((prev) => !prev)}
            />
          </GlobalSidebarContainer>
          {selectedMode === "submissions" ? <Iframe
            id="waymore-forms-Iframe"
            className={`waymore-forms-Iframe`}
            url={`${waymoreFormsURL}/?id=${sessionStorage.getItem('submissionId')}`}
            styles={{ width: isSidebarOpen ? "calc(100vw - 280px)" : "calc(100vw - 52px)", transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)" }}
          /> :
            <GlobalContentContainer item isCollapsed={!isSidebarOpen}>
              <div className={`workspace-container ${openDeletePopup ? "disableScroll" : ""}`}>
                <RootContainer>
                  <WorkspaceHeader
                    setLoading={setIsLoading}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    sortByDateOption={sortByDateOption}
                    setSortByDateOption={setSortByDateOption}
                    filterOption={filterOption}
                    setFilterOption={setFilterOption}
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    selectedMode={selectedMode}
                    setCreateNewFormPopup={setCreateNewFormPopup}
                    isLoading={isLoading}
                    deleteForeverClickHandler={() => {
                      setOpenDeletePopup(true);
                      setMultiDeleteArr(dataProvider.deleted.map((form: any) => form.formUUID))
                    }}
                  />
                  <BodyContainer
                    className='hide-scroll'
                    isSmallScreen={isLessThan1280}
                    isListEmpty={isListEmpty}
                    isListSearchActive={isListSearchActive}
                  >
                    {renderCards()}
                  </BodyContainer>
                </RootContainer>
              </div>
            </GlobalContentContainer>}
        </GlobalBodyContainer>
        <GlobalFooter
          isOpen={multiDelete}
          multiDeleteArr={multiDeleteArr}
          handleCancel={handleCancel}
          handlepublishForm={() => publishForm(multiDeleteArr)}
          openDeleteConfirmPopup={() => setOpenDeletePopup(true)}
          handleMoveToDraft={() => handleMoveToDraft(multiDeleteArr)}
        />
      </GlobalRootContainer>
      <DeleteConfirmationModal
        isOpen={openDeletePopup}
        cancelHandler={handleCancel}
        submitHandler={handleMultiDelete}
        cancelText="Cancel"
        submitText="Delete"
        contentText="Are you sure that you want to delete the selected item(s)?"
      />
      {createNewFormPopup && <CreateNewFormPopup open={createNewFormPopup} setOpen={setCreateNewFormPopup} setTosterOpen={setTosterOpen} getFormDatas={getFormDatas} />}
      <Snackbar
        open={tosterOpen.open}
        autoHideDuration={tosterOpen.undoAction ? 6000 : 3000}
        onClose={(_event: any, reason?: string) => {
          if (reason === 'clickaway') return;
          setTosterOpen({ open: false, message: "", severity: "", undoAction: null });
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          variant="filled"
          onClose={() => {
            setTosterOpen({ open: false, message: "", severity: "", undoAction: null });
          }}
          severity={tosterOpen.severity}
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            alignItems: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-action': {
              paddingTop: 0,
              paddingBottom: 0,
              display: 'flex',
              alignItems: 'center'
            }
          }}
          action={
            tosterOpen.undoAction ? (
              <Button
                color="inherit"
                size="small"
                sx={{
                  color: 'white',
                  borderRadius: 1,
                  border: "1px solid rgba(255, 255, 255, 0.6)",
                  textTransform: 'none',
                  marginRight: "7px",
                  padding: '1px 10px',
                  minWidth: 'auto',
                  "&:hover": {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderColor: 'white'
                  }
                }}
                onClick={() => {
                  if (tosterOpen.undoAction) {
                    tosterOpen.undoAction();
                  }
                }}
              >
                undo
              </Button>
            ) : null
          }
        >
          {tosterOpen.message}
        </Alert>
      </Snackbar>
      <Backdrop
        open={false}
        sx={{ color: "#fff", zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Fragment>
  )
}

export default Workspace